import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';

import {
  allReady,
  useDataRequestsStore,
  usePartnersStore,
  usePopulationsStore,
} from '@/stores';

import useClearbit from './useClearbit';

export default function useRequests() {
  const dataRequestsStore = useDataRequestsStore();
  const partnersStore = usePartnersStore();
  const populationsStore = usePopulationsStore();

  const loading = computed(
    () => !allReady(dataRequestsStore, partnersStore).value,
  );

  const { outboundShareRequests, inboundShareRequests } =
    storeToRefs(dataRequestsStore);
  const { proposalsSent, proposalsReceived } = storeToRefs(partnersStore);

  /* Data Requests */
  const hasDataShareRequests = computed(
    () =>
      allPendingInboundDataShareRequests.value.length ||
      allPendingOutboundDataShareRequests.value.length,
  );
  const allPendingInboundDataShareRequests = computed(() =>
    inboundShareRequests.value.filter(
      (shareRequest) =>
        shareRequest.status === 'pending' &&
        !!partnersStore.getPartnerOrgById(shareRequest.partner_org_id) &&
        !!populationsStore.getPopulationById(shareRequest.population_id),
    ),
  );

  const allPendingOutboundDataShareRequests = computed(() =>
    outboundShareRequests.value
      .filter(
        (shareRequest) =>
          shareRequest.status === 'pending' &&
          !!populationsStore.getPartnerPopulationById(
            shareRequest.partner_population_id,
          ),
      )
      .map((r) => ({ ...r, is_outbound: true })),
  );

  /* Partnership Requests */
  const hasPartnershipRequests = computed(
    () =>
      proposalsReceived.value.length ||
      allPendingOutboundPartnershipProposals.value.length,
  );
  /**
   * Groups proposals by company, keeping only the most recent per company.
   * When multiple contacts were invited at the same company, the row collapses
   * into a single representative and contact_name is nulled out.
   */
  function groupProposalsByCompany(status) {
    const proposalsByCompany = new Map();
    proposalsSent.value
      .filter((proposal) => proposal.status === status)
      .forEach((proposal) => {
        const company = proposal.contact_company;
        const group = proposalsByCompany.get(company);
        if (group) group.push(proposal);
        else proposalsByCompany.set(company, [proposal]);
      });

    return [...proposalsByCompany.values()].map((group) => {
      const mostRecent = group.reduce((latest, proposal) =>
        proposal.created_at > latest.created_at ? proposal : latest,
      );
      return {
        ...mostRecent,
        is_outbound: true,
        contact_name: group.length > 1 ? null : mostRecent.contact_name,
      };
    });
  }

  const allPendingOutboundPartnershipProposals = computed(() =>
    groupProposalsByCompany('pending'),
  );
  const declinedOutboundPartnershipProposals = computed(() =>
    groupProposalsByCompany('declined'),
  );

  const allPendingPartnershipProposals = computed(() => [
    ...allPendingOutboundPartnershipProposals.value,
    ...proposalsReceived.value,
  ]);

  /* Get icons for outbound requests from clearbit */
  const outboundData = ref([]);
  const outboundRequestMap = computed(() =>
    outboundData.value.reduce((agg, { companyName, company }) => {
      return {
        ...agg,
        [companyName]: company,
      };
    }, {}),
  );

  /* This is tech debt! Ideally we modify the outbound partnerships and data
   * sharing endpoints to provide the clearbit domain in the response so we don't
   * have to join this data with a second API call */
  const { searchIcons } = useClearbit(false);
  async function fetchOutboundIcons() {
    const companies = [
      ...allPendingOutboundPartnershipProposals.value,
      ...declinedOutboundPartnershipProposals.value,
    ].map((c) => c.contact_company);
    const promises = companies.map(async (companyName) => {
      const companyResults = await searchIcons(companyName);
      const company = companyResults.find((c) => c.name === companyName);
      return { companyName, company };
    });

    outboundData.value = await Promise.all(promises);
  }

  const totalInboundRequests = computed(
    () =>
      allPendingInboundDataShareRequests.value.length +
      proposalsReceived.value.length,
  );

  return {
    loading,
    hasDataShareRequests,
    allPendingOutboundDataShareRequests,
    allPendingInboundDataShareRequests,
    allPendingOutboundPartnershipProposals,
    declinedOutboundPartnershipProposals,
    hasPartnershipRequests,
    allPendingInboundPartnershipProposals: proposalsReceived,
    allPendingPartnershipProposals,
    outboundRequestMap,
    fetchOutboundIcons,
    totalInboundRequests,
  };
}
